import { useMemo } from 'react';

import { useSession } from '@ais-ui/websockets';
import { UseQueryResult } from '@tanstack/react-query';
import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';

import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';

import {
  Configuration,
  type GetPss200Response,
  PssApi,
} from '@ais-ui/api-spec/config';
import { getBasePath } from './getAISBasePath';
import { getAuthHeaderFromCredentials } from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';

const usePSSApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { hostname } = useBasePath();
  const apiClientWrapper = useApiClientWrapper();

  let basePath = getBasePath();
  let configuration: Configuration;
  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/config`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/config`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const PSSApi = useMemo(() => new PssApi(configuration), [configuration]);
  return apiClientWrapper(PSSApi) as PssApi;
};

export const useFetchAllPSS = (): UseQueryResult<GetPss200Response> => {
  const pssApi = usePSSApi();
  return useQuery({
    queryKey: ['FetchAllPSS'],
    queryFn: async () => {
      return await pssApi.getPss();
    },
  }, 'pss:listAll');
};

export const useFetchPSSWithType = (
  type: string
): UseQueryResult<GetPss200Response> => {
  const pssApi = usePSSApi();
  return useQuery({
    queryKey: ['FetchWithTypePSS', type],
    queryFn: async () => {
      return await pssApi.getPss({
        type,
        tx: true,
      });
    },
  }, 'pss:listByType');
};
